/**
 * Sandbox overlay + rename cascade (PR Q4 · §Q4.3).
 *
 * `overlayDiff(manifest, ops)` — distils a sandbox op list into the
 * minimum data the constellation overlay needs:
 *   - affectedEntities · entity ids touched by any op
 *   - renamedFields   · { entityId, oldName, newName }[]
 *   - brokenRefs      · refs that no longer resolve after the ops
 *
 * `renameCascade(manifest, entityId, oldField, newField)` — when the
 * user is about to rename a field, surface the references that would
 * break so the UI can warn inline and offer a one-click cascade.
 *
 * Reference semantics: references in the manifest carry `from` + `to`
 * (entity ids) + `fromField` (the source-side field that holds the
 * reference). They do NOT carry a target-side field name, so target
 * renames don't break refs structurally; source renames do.
 */
import type { AtlasManifest, Reference } from '@atlas-manifest/types';
import type { SandboxOp } from './sandboxOps.js';

export interface OverlayDiff {
  /** Entity ids touched by any op (deduped, alphabetical). */
  affectedEntities: string[];
  /** Renamed fields, in op order. */
  renamedFields: Array<{ entityId: string; oldName: string; newName: string }>;
  /** References that no longer resolve after the ops apply. */
  brokenRefs: Array<{ from: string; to: string; field: string; reason: string }>;
}

export function overlayDiff(manifest: AtlasManifest, ops: readonly SandboxOp[]): OverlayDiff {
  const affected = new Set<string>();
  const renamedFields: OverlayDiff['renamedFields'] = [];
  const brokenRefs: OverlayDiff['brokenRefs'] = [];

  // Track per-(entity, field) state so subsequent ops can stack cleanly.
  // For PR Q4 we keep it simple: just walk ops, compute the union effects.
  for (const op of ops) {
    // Listed by kind (not a Set + cast) so a future SandboxOp variant with no
    // entityId forces a compile error here instead of silently reaching
    // `op.entityId` below — the switch narrows op's type by exhaustion.
    switch (op.kind) {
      case 'create-domain':
      case 'create-reference':
      case 'create-surface':
      case 'create-cloud-function':
      case 'create-event':
      case 'create-tag':
      case 'create-state-machine':
      case 'update-surface':
      case 'update-cloud-function':
      case 'update-event':
      case 'update-path':
      case 'add-payload-field':
      case 'remove-payload-field':
      case 'rename-payload-field':
      case 'change-payload-field-type':
      case 'add-function-entity-ref':
      case 'remove-function-entity-ref':
      case 'add-event-carried-entity':
      case 'remove-event-carried-entity':
      case 'link-feature-flag':
      case 'unlink-feature-flag':
        continue; // targets a Feature, not an Entity — no entityId
    }
    affected.add(op.entityId);
    if (op.kind === 'rename-field') {
      renamedFields.push({ entityId: op.entityId, oldName: op.from, newName: op.to });
      for (const r of brokenRefsFromMissingField(manifest.references, op.entityId, op.from, 'renamed')) {
        brokenRefs.push(r);
      }
    } else if (op.kind === 'remove-field') {
      for (const r of brokenRefsFromMissingField(manifest.references, op.entityId, op.fieldName, 'removed')) {
        brokenRefs.push(r);
      }
    }
  }

  return {
    affectedEntities: [...affected].sort(),
    renamedFields,
    brokenRefs,
  };
}

function brokenRefsFromMissingField(
  references: readonly Reference[],
  entityId: string,
  fieldName: string,
  cause: 'renamed' | 'removed',
): OverlayDiff['brokenRefs'] {
  return references
    .filter((r) => r.from === entityId && r.fromField === fieldName)
    .map((r) => ({
      from: r.from,
      to: r.to,
      field: r.fromField,
      reason: `${entityId}.${fieldName} ${cause}; reference no longer resolves.`,
    }));
}

export interface RenameCascade {
  /** Refs whose source field matches the renamed field. */
  affectedRefs: Reference[];
  /** Auto-suggested compensating ops (typically empty in v1). */
  suggestedOps: SandboxOp[];
}

export function renameCascade(
  manifest: AtlasManifest,
  entityId: string,
  oldField: string,
  _newField: string,
): RenameCascade {
  const affectedRefs = manifest.references.filter(
    (r) => r.from === entityId && r.fromField === oldField,
  );
  // V1: surface the affected refs as inline warnings; we don't auto-suggest
  // compensating ops because the manifest schema is the source of truth and
  // editing it programmatically would diverge from the form-driven flow.
  // The Q4 spec leaves this empty by design.
  return { affectedRefs, suggestedOps: [] };
}
